import { hasPermission } from '@hq/permission';
import { Button, Result, Spin } from 'antd';
import { Link, Navigate, createBrowserRouter, useLocation } from 'react-router-dom';
import type { ReactElement } from 'react';
import { lazy, Suspense } from 'react';
import AppLayout from '../layouts/AppLayout';
import Login from '../pages/Login';
import ScreenDevices from '../pages/ScreenDevices';
import ScreenScan from '../pages/ScreenScan';
import { useAuthStore } from '../session';

// 含 ECharts 的页面懒加载，首屏不下载 charts 分块（docs/05 §6-12）
const Overview = lazy(() => import('../pages/Overview'));
const RankPage = lazy(() => import('../pages/GovPanels').then((m) => ({ default: m.RankPage })));
const StructurePage = lazy(() => import('../pages/GovPanels').then((m) => ({ default: m.StructurePage })));
const PerformancePage = lazy(() => import('../pages/GovPanels').then((m) => ({ default: m.PerformancePage })));
const DrillPage = lazy(() => import('../pages/GovPanels').then((m) => ({ default: m.DrillPage })));
const TopicPage = lazy(() => import('../pages/GovPanels').then((m) => ({ default: m.TopicPage })));
const ReportPage = lazy(() => import('../pages/GovPanels').then((m) => ({ default: m.ReportPage })));
const GroupPage = lazy(() => import('../pages/GovPanels').then((m) => ({ default: m.GroupPage })));
// 监管大屏（/screen）：图表重、单独懒加载，登录端首屏不携带大屏代码
const ScreenPage = lazy(() => import('../pages/ScreenPage'));

function LazyPage({ children }: { children: ReactElement }) {
  return <Suspense fallback={<Spin style={{ display: 'block', margin: '80px auto' }} />}>{children}</Suspense>;
}

function RequireAuth({ children }: { children: ReactElement }) {
  const token = useAuthStore((state) => state.token);
  const location = useLocation();
  if (!token) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return children;
}

/**
 * 路由级权限门禁（纵深防御）：侧栏只是裁剪入口，直接敲 URL 仍能进页面，
 * 用户会看到满屏 403。后端始终校验，这里把失败提前成一句明确提示。
 * 取值与 `layouts/AppLayout` 的 MENU `perm` 完全一致（`/overview` 无需权限码）。
 */
const ROUTE_PERMS: Record<string, string[]> = {
  rank: ['wl:analytics:view'],
  structure: ['wl:analytics:view'],
  performance: ['wl:analytics:view'],
  drill: ['wl:analytics:view'],
  topic: ['wl:analytics:view'],
  report: ['wl:analytics:view'],
  group: ['wl:analytics:view'],
  'screen-devices': ['wl:analytics:view'],
  'screen-scan': ['wl:analytics:view']
};

function RequirePerm({ path, children }: { path: string; children: ReactElement }) {
  const profile = useAuthStore((state) => state.profile);
  const required = ROUTE_PERMS[path];

  if (!required?.length || profile?.level === 'admin') return children;

  const allowed = profile?.perms ? required.some((perm) => hasPermission(profile.perms, perm)) : false;
  if (allowed) return children;

  return (
    <Result
      status="403"
      title="无权访问"
      subTitle={`当前账号缺少 ${required.join(' 或 ')} 权限，请联系系统管理员。`}
      extra={
        <Link to="/overview">
          <Button type="primary">返回全区总览</Button>
        </Link>
      }
    />
  );
}

export const router = createBrowserRouter([
  { path: '/login', element: <Login /> },
  {
    // 大屏全屏路由：不套 AppLayout、不走账号登录态门禁（屏端凭「设备凭据」独立换只读令牌），
    // 未授权时页内展示激活页（屏端凭设备凭据独立取数，与账号登录态无关）
    path: '/screen',
    element: (
      <LazyPage>
        <ScreenPage />
      </LazyPage>
    )
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to="/overview" replace /> },
      {
        path: 'overview',
        element: (
          <LazyPage>
            <Overview />
          </LazyPage>
        )
      },
      {
        path: 'rank',
        element: (
          <RequirePerm path="rank">
            <LazyPage>
              <RankPage />
            </LazyPage>
          </RequirePerm>
        )
      },
      {
        path: 'structure',
        element: (
          <RequirePerm path="structure">
            <LazyPage>
              <StructurePage />
            </LazyPage>
          </RequirePerm>
        )
      },
      {
        path: 'performance',
        element: (
          <RequirePerm path="performance">
            <LazyPage>
              <PerformancePage />
            </LazyPage>
          </RequirePerm>
        )
      },
      {
        path: 'drill',
        element: (
          <RequirePerm path="drill">
            <LazyPage>
              <DrillPage />
            </LazyPage>
          </RequirePerm>
        )
      },
      {
        path: 'topic',
        element: (
          <RequirePerm path="topic">
            <LazyPage>
              <TopicPage />
            </LazyPage>
          </RequirePerm>
        )
      },
      {
        path: 'report',
        element: (
          <RequirePerm path="report">
            <LazyPage>
              <ReportPage />
            </LazyPage>
          </RequirePerm>
        )
      },
      {
        path: 'group',
        element: (
          <RequirePerm path="group">
            <LazyPage>
              <GroupPage />
            </LazyPage>
          </RequirePerm>
        )
      },
      {
        path: 'screen-devices',
        element: (
          <RequirePerm path="screen-devices">
            <LazyPage>
              <ScreenDevices />
            </LazyPage>
          </RequirePerm>
        )
      },
      {
        path: 'screen-scan',
        element: (
          <RequirePerm path="screen-scan">
            <LazyPage>
              <ScreenScan />
            </LazyPage>
          </RequirePerm>
        )
      }
    ]
  },
  { path: '*', element: <Navigate to="/overview" replace /> }
]);
